
import { useState } from 'react';

interface Package {
  id: string;
  name: string;
  price: number;
  maxPrice: number;
  targetReturn: string;
  timeline: string;
  description: string;
  features: string[];
  investments: string[];
  benefits: string[];
  ideal: string;
  popular?: boolean;
}

interface InvestmentCalculatorProps {
  packages: Package[];
}

export default function InvestmentCalculator({ packages }: InvestmentCalculatorProps) {
  const [selectedPackage, setSelectedPackage] = useState('growth');
  const [investmentAmount, setInvestmentAmount] = useState(75000);
  const [investmentYears, setInvestmentYears] = useState(3);

  const getReturnRange = (packageId: string) => {
    switch (packageId) {
      case 'starter':
        return { min: 15, max: 18 };
      case 'growth':
        return { min: 18, max: 22 };
      case 'premium':
        return { min: 22, max: 25 };
      default:
        return { min: 18, max: 22 };
    }
  };

  const calculateReturns = () => {
    const returnRange = getReturnRange(selectedPackage);
    const minReturn = investmentAmount * Math.pow(1 + returnRange.min / 100, investmentYears);
    const maxReturn = investmentAmount * Math.pow(1 + returnRange.max / 100, investmentYears);
    const minProfit = minReturn - investmentAmount;
    const maxProfit = maxReturn - investmentAmount;
    
    return {
      minTotal: minReturn,
      maxTotal: maxReturn,
      minProfit,
      maxProfit,
      minAnnual: minProfit / investmentYears,
      maxAnnual: maxProfit / investmentYears
    };
  };

  const results = calculateReturns();
  const selectedPkg = packages.find(pkg => pkg.id === selectedPackage);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Investment Calculator</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Calculate your potential returns based on different investment packages and amounts.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Calculator Inputs */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Investment Parameters</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Investment Package
                </label>
                <select
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {packages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.name} ({pkg.targetReturn} returns)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Investment Amount: ${investmentAmount.toLocaleString()}
                </label>
                <input
                  type="range"
                  min={selectedPkg?.price || 25000}
                  max={selectedPkg?.maxPrice || 500000}
                  step="5000"
                  value={investmentAmount}
                  onChange={(e) => setInvestmentAmount(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>${selectedPkg?.price.toLocaleString()}</span>
                  <span>${selectedPkg?.maxPrice.toLocaleString()}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Investment Period: {investmentYears} years
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  value={investmentYears}
                  onChange={(e) => setInvestmentYears(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 year</span>
                  <span>10 years</span>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Projected Returns</h3>
            
            <div className="space-y-6">
              <div className="bg-emerald-50 rounded-lg p-6">
                <h4 className="font-semibold text-emerald-800 mb-4">Total Investment Value</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-emerald-600">Conservative</div>
                    <div className="text-2xl font-bold text-emerald-800">
                      ${results.minTotal.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-emerald-600">Optimistic</div>
                    <div className="text-2xl font-bold text-emerald-800">
                      ${results.maxTotal.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <h4 className="font-semibold text-blue-800 mb-4">Total Profit</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-blue-600">Conservative</div>
                    <div className="text-2xl font-bold text-blue-800">
                      ${results.minProfit.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-blue-600">Optimistic</div>
                    <div className="text-2xl font-bold text-blue-800">
                      ${results.maxProfit.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-6">
                <h4 className="font-semibold text-purple-800 mb-4">Annual Profit</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-purple-600">Conservative</div>
                    <div className="text-2xl font-bold text-purple-800">
                      ${results.minAnnual.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-purple-600">Optimistic</div>
                    <div className="text-2xl font-bold text-purple-800">
                      ${results.maxAnnual.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <i className="ri-information-line text-amber-600 text-lg mr-2 mt-1"></i>
                <div className="text-sm text-amber-800">
                  <strong>Disclaimer:</strong> These calculations are projections based on historical performance 
                  and market analysis. Actual returns may vary and past performance does not guarantee future results.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
